import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type PixelRect,
  type Point,
  rectFromPoints,
  type Size,
} from "../lib/selection-geometry";

/** Below this a drag is a click that dismisses the selection, not a marquee. */
const MIN_DRAG_PX = 8;

/** Pointer position relative to the element the handler is bound to. */
function pointIn(e: ReactPointerEvent<HTMLElement>): Point {
  const bounds = e.currentTarget.getBoundingClientRect();
  return { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
}

export interface MarqueeResult {
  rectPx: PixelRect;
  /** The frame's size at pointer-down, so the rect can be normalized. */
  frame: Size;
}

export interface MarqueeState {
  isDragging: boolean;
  /** The drawn rect, kept after pointer-up so it stays visible under the menu. */
  rectPx: PixelRect | null;
  reset: () => void;
  handlers: {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
  };
}

/**
 * Pointer-drag state for the marquee. The element these handlers are spread
 * onto must exactly cover the stage frame, since its bounding rect is what
 * pointer positions are measured against.
 *
 * `onComplete` fires only for a real drag; a click-sized drag resets instead.
 */
export function useSelectionMarquee(
  onComplete: (result: MarqueeResult) => void,
): MarqueeState {
  const [start, setStart] = useState<Point | null>(null);
  const [current, setCurrent] = useState<Point | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const frameRef = useRef<Size | null>(null);

  const reset = useCallback(() => {
    setStart(null);
    setCurrent(null);
    setIsDragging(false);
    frameRef.current = null;
  }, []);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const bounds = e.currentTarget.getBoundingClientRect();
    frameRef.current = { width: bounds.width, height: bounds.height };
    const point = { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
    // Capture so the drag survives the pointer leaving the frame.
    e.currentTarget.setPointerCapture(e.pointerId);
    setStart(point);
    setCurrent(point);
    setIsDragging(true);
  }, []);

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!isDragging) return;
      setCurrent(pointIn(e));
    },
    [isDragging],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!isDragging) return;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      setIsDragging(false);

      const end = pointIn(e);
      const frame = frameRef.current;
      if (!(start && frame)) return reset();

      const rectPx = rectFromPoints(start, end);
      if (rectPx.width < MIN_DRAG_PX && rectPx.height < MIN_DRAG_PX) {
        return reset();
      }
      setCurrent(end);
      onComplete({ rectPx, frame });
    },
    [isDragging, start, onComplete, reset],
  );

  const onPointerCancel = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      reset();
    },
    [reset],
  );

  const handlers = useMemo(
    () => ({ onPointerDown, onPointerMove, onPointerUp, onPointerCancel }),
    [onPointerDown, onPointerMove, onPointerUp, onPointerCancel],
  );

  return {
    isDragging,
    rectPx: useMemo(
      () => (start && current ? rectFromPoints(start, current) : null),
      [start, current],
    ),
    reset,
    handlers,
  };
}
